import { V2Position, V2Vault } from '@/core/migrate';
import { Vault, feeTierToPercentage } from 'nftx.js';
import Button from '../../elements/Button';
import VaultIcon from '../../elements/VaultIcon';
import { useFormatter } from 'use-intl';
import { ExclamationTriangle } from '../../elements/Icon/icons/ExclamationTriangle';
import { formatEther } from 'viem';
import FormattedEther from '../../elements/FormattedEther';
import Price from '../../elements/Price';
import Icon, { Icons } from '../../elements/Icon';
import { useRef, useState } from 'react';
import useInventoryPools from '@/application/read/pools/useInventoryPools';

const MigrateInventory = ({
  position,
  v2Vault,
  v3VaultId,
  setV3VaultId,
  vaults,
  onSubmit,
  withinModal,
}: {
  position: V2Position;
  v2Vault: V2Vault;
  v3VaultId: string;
  setV3VaultId: (v: string) => void;
  vaults: Vault[];
  onSubmit: () => void;
  withinModal?: boolean;
}) => {
  const [showVaults, setShowVaults] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { number } = useFormatter();
  const vaultIds = position.migrateOptions.map((option) => option.vaultId);
  const { data: allPools } = useInventoryPools();
  const pools = allPools?.filter((pool) => vaultIds.includes(pool.vaultId));
  const option = position.migrateOptions.find(
    (option) => option.vaultId === v3VaultId
  );

  if (!option) {
    return <div>There are no available vaults for this position</div>;
  }

  const fixedVault = vaults.length === 1;
  const selectedVault = vaults.find((vault) => vault.vaultId === v3VaultId);
  const selectedPool = pools?.find((pool) => pool.vaultId === v3VaultId);
  const remainingVToken = position.vToken - option.maxVToken;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="space-y-4"
    >
      <div className="bg-gray-50 border border-gray-300 rounded-xl p-4">
        <div className="flex space-x-4 items-center font-bold">
          <h3 className="text-lg">Existing position</h3>
          <div className="bg-gray-100 p-2 rounded-lg flex items-center flex-grow">
            <div className="flex items-center mr-2">
              <VaultIcon
                vaultId={position.vaultId}
                legacy={true}
                className="w-4 h-4 mr-2"
              />
              <span className="font-bold">{`${v2Vault.token.name}`}</span>
            </div>

            <div className="ml-auto">
              <span className="mr-2">
                {`${number(Number(formatEther(position.vToken)), {
                  maximumFractionDigits: 2,
                })} ${position.symbol}`}
              </span>
              <span className="text-gray-400 text-xs uppercase">Available</span>
            </div>
          </div>
        </div>
        <div className="text-center text-sm text-gray-500 mt-4">
          <ExclamationTriangle className="w-5 h-5 inline-block mr-2" />
          {`Only whole NFT units can be migrated. `}
          <b className="font-bold">{`${number(
            Number(formatEther(remainingVToken)),
            { maximumFractionDigits: 2 }
          )} ${v2Vault.token.symbol} will stay in your NFTX V2 position.`}</b>
        </div>
      </div>

      <div className="bg-gray-50 border border-gray-300 rounded-xl p-4">
        <hgroup className="flex items-center space-x-4 mb-4">
          <h3 className="text-lg font-bold flex-grow">New Position</h3>
          <span className="ml-auto text-gray-400">
            {'Select vault to deposit to'}
          </span>
        </hgroup>

        <div className="relative mb-4" ref={dropdownRef}>
          <button
            type="button"
            className="bg-gray-100 border border-gray-300 rounded-md py-6 px-6 flex items-center w-full"
            onClick={() => setShowVaults((x) => !x)}
            disabled={fixedVault}
          >
            <VaultIcon vaultId={selectedVault?.vaultId} className="w-6 h-6" />

            <span className="mx-2 font-bold">
              {selectedVault?.token.symbol}
            </span>
            <span className="uppercase inline-flex items-baseline space-x-1 mr-4 ml-auto">
              <b className="font-bold">{selectedVault?.totalHoldings}</b>{' '}
              <span className="text-xs font-medium text-gray-500">{`holdings`}</span>
            </span>
            <span className="uppercase inline-flex items-baseline space-x-1 mr-4">
              <b className="font-bold">
                <FormattedEther
                  value={selectedPool?.apr['1m']}
                  style="percent"
                />
              </b>
              <span className="text-xs font-medium text-gray-500">{`APR`}</span>
            </span>
            <span className="truncate mr-4">
              <b className="font-bold truncate">
                <Price value={selectedPool?.vTokenValue} />
              </b>{' '}
              <span className="text-xs font-medium text-gray-500">{`TVL`}</span>
            </span>
            <span className="ml-4">
              {!fixedVault ? (
                <Icon
                  name={Icons.CHEVRON_DOWN}
                  className="text-gray-400"
                  size={24}
                />
              ) : (
                <Icon
                  name={Icons.CHECK_CIRCLE}
                  className="text-green-500"
                  size={32}
                />
              )}
            </span>
          </button>
          {showVaults && (
            <div
              className={`overflow-hidden bg-gray-100 border border-gray-300 rounded-md w-full absolute top-full left-0 right-0 ${
                withinModal ? 'h-64' : 'h-auto'
              } mt-4 shadow-lg flex flex-col z-10`}
            >
              <div className="flex text-xs text-gray-500 font-medium uppercase mt-2 px-3">
                <div className="w-1/4 p-1">{'Vault'}</div>
                <div className="w-1/4 p-1 text-right">{'Holdings'}</div>
                <div className="w-1/4 p-1 text-right">{'APR'}</div>
                <div className="w-1/4 p-1 text-right">{'TVL'}</div>
              </div>
              <div className="flex-1 overflow-scroll hide-scroll text-sm space-y-1 py-2">
                {position.migrateOptions.map((v) => {
                  const vaultId = v.vaultId;
                  const vault = vaults.find(
                    (vault) => vault.vaultId === vaultId
                  );
                  const pool = pools?.find((pool) => pool.vaultId === vaultId);
                  if (!vault) {
                    return null;
                  }

                  return (
                    <button
                      key={vaultId}
                      value={v.vaultId}
                      onClick={() => {
                        setV3VaultId(vaultId);
                        setShowVaults(false);
                      }}
                      className="flex items-center w-full px-3 hover:bg-gray-50 font-medium"
                    >
                      <span className="w-1/4 p-1 text-left">
                        {`${vault?.token.symbol}`}{' '}
                      </span>

                      <span className="w-1/4 p-1 text-right">{`${vault?.totalHoldings}`}</span>
                      <span className="w-1/4 p-1 text-right truncate">
                        <FormattedEther
                          value={pool?.apr['1m']}
                          style="percent"
                        />
                      </span>
                      <span className="w-1/4 p-1 text-right truncate">
                        <Price value={pool?.vTokenValue} />
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <dl className="bg-gray-50 border border-gray-300 rounded-xl p-4 space-y-2">
        <div className="flex items-baseline space-x-2">
          <dt className="text-gray-500 font-medium text-sm">Migrating</dt>
          <span className="flex-1 border-b border-dotted border-gray-300"></span>
          <dd className="font-medium">
            <FormattedEther value={option.maxVToken} />
            {` ${selectedVault?.token.symbol}`}
          </dd>
        </div>
        <div className="flex items-baseline space-x-2">
          <dt className="text-gray-500 font-medium text-sm">
            Remaining V2 Balance
          </dt>
          <span className="flex-1 border-b border-dotted border-gray-300"></span>
          <dd className="font-medium">
            <FormattedEther value={remainingVToken} />{' '}
            {`${selectedVault?.token.symbol}`}
          </dd>
        </div>
      </dl>
      <div>
        <Button
          type="submit"
          disabled={!v3VaultId}
          className="w-full"
          size="large"
        >
          Migrate
        </Button>
      </div>
    </form>
  );
};

export default MigrateInventory;
